<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphCallout } from '@/components/base/callout';
import { SphField } from '@/components/base/field';
import { SphLink } from '@/components/base/link';
import { SphSelect } from '@/components/base/select';
import { SphSpinner } from '@/components/base/spinner';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { useDialog } from '@/plugins/dialog';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    kindergartenId: number
    employeeManagementUrl: string
    homepageUrl: string
}>();

const ROLES = [
    'admin',
    'executive',
    'secondExecutive',
    'agent',
    'educationalAdviser',
    'privacy',
    'fireProtection',
    'representativeFireProtection',
    'firstAidAuthorized',
    'secondAidAuthorized',
    'safetyOfficer',
] as const;

const NAME_PLACEHOLDER = /%name%/g;

type Role = typeof ROLES[number];
type Assignment = Record<Role, number | null>;

const { i18n } = useI18n();
const { notify } = useNotifications();
const { dialog } = useDialog();

const url = `/api/kindergartens/${props.kindergartenId}/responsibilities`;

const loading = ref(true);
const pending = ref(false);
const errors = ref<Partial<Record<Role, string[]>>>({});
const employees = ref<{ id: number, name: string }[]>([]);
const adminCandidateIds = ref<number[]>([]);
const canChangeAdmin = ref(false);
const savedAdmin = ref<number | null>(null);
const assignment = ref<Assignment>({
    admin: null,
    executive: null,
    secondExecutive: null,
    agent: null,
    educationalAdviser: null,
    privacy: null,
    fireProtection: null,
    representativeFireProtection: null,
    firstAidAuthorized: null,
    secondAidAuthorized: null,
    safetyOfficer: null,
});

const employeeOptions = computed(() => employees.value.map((employee) => ({ value: employee.id, label: employee.name })));
const adminOptions = computed(() => employeeOptions.value.filter((option) => adminCandidateIds.value.includes(option.value)));
const secondExecutiveOptions = computed(() => employeeOptions.value.filter((option) => option.value !== assignment.value.executive));

const management: { role: Role, label: string }[] = [
    { role: 'executive', label: 'Einrichtungsleitung' },
    { role: 'secondExecutive', label: 'Einrichtungsleitung 2' },
    { role: 'agent', label: 'Stellv. Einrichtungsleitung' },
    { role: 'educationalAdviser', label: 'Pädagogische Fachberatung' },
];

const officers: { role: Role, label: string }[] = [
    { role: 'privacy', label: 'Datenschutzbeauftragte(r)' },
    { role: 'fireProtection', label: 'Brandschutzbeauftragte(r)' },
    { role: 'representativeFireProtection', label: 'Stellv. Brandschutzbeauftragte(r)' },
    { role: 'firstAidAuthorized', label: 'Erste-Hilfe Beauftragte(r)' },
    { role: 'secondAidAuthorized', label: 'Stellv. Erste-Hilfe Beauftragte(r)' },
    { role: 'safetyOfficer', label: 'Sicherheitsbeauftragte(r)' },
];

// SphSelect's empty option is '', the API's is null.
function selected(role: Role): number | '' {
    return assignment.value[role] ?? '';
}

function select(role: Role, value: unknown) {
    assignment.value[role] = value === '' || value === null || value === undefined ? null : Number(value);
}

function optionsFor(role: Role) {
    return role === 'secondExecutive' ? secondExecutiveOptions.value : employeeOptions.value;
}

onMounted(() => {
    load();
});

async function load() {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Request failed');
        }

        const { data } = await response.json();
        employees.value = data.employees;
        adminCandidateIds.value = data.adminCandidateIds;
        canChangeAdmin.value = data.canChangeAdmin;
        savedAdmin.value = data.admin;
        for (const role of ROLES) {
            assignment.value[role] = data[role];
        }
    } catch {
        notify.error('Daten konnten nicht geladen werden');
    } finally {
        loading.value = false;
    }
}

async function confirmAdminChange(): Promise<boolean> {
    if (assignment.value.admin === savedAdmin.value) {
        return true;
    }

    const successor = employees.value.find((employee) => employee.id === assignment.value.admin)?.name ?? '';

    return await dialog.confirm({
        title: i18n.t('Administrator ändern'),
        message: i18n.t('Sie sind im Begriff, Ihre Adminrechte an %name% abzugeben. Dieser Schritt kann nur von %name% rückgängig gemacht werden.')
            .replace(NAME_PLACEHOLDER, () => successor),
    }) === true;
}

async function save() {
    if (!await confirmAdminChange()) {
        return;
    }

    const adminChanged = assignment.value.admin !== savedAdmin.value;
    pending.value = true;
    errors.value = {};
    try {
        const response = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(assignment.value),
        });

        if (response.status === 400) {
            const { error } = await response.json();
            for (const detail of error.details) {
                const attributeName = detail.find((d: { key: string }) => d.key === 'attributeName')?.value as Role | undefined;
                const message = detail.find((d: { key: string }) => d.key === 'constraintMessage')?.value;
                if (attributeName && message) {
                    errors.value[attributeName] = [message];
                }
            }
            return;
        }

        if (!response.ok) {
            throw new Error('Request failed');
        }

        // Handing the admin role on can take the sub-admin rights this page needs with it.
        if (adminChanged && (await fetch(url)).status === 403) {
            notify.success('saveChanges.success', {}, { saveInSession: true });
            window.location.href = props.homepageUrl;
            return;
        }

        notify.success('saveChanges.success');
        await load();
    } catch {
        notify.error('saveChanges.error');
    } finally {
        pending.value = false;
    }
}
</script>

<template>
    <div>
        <SphSpinner
            v-if="loading"
            variant="centered"
        />
        <div
            v-else
            class="flex flex-col gap-6"
        >
            <SphCallout
                type="info"
                :title="i18n.t('Bitte lege die Mitarbeitenden zuerst in der Mitarbeiterverwaltung an.')"
            >
                <template #actions>
                    <SphLink
                        :href="props.employeeManagementUrl"
                        :label="i18n.t('Mitarbeiterverwaltung öffnen')"
                    />
                </template>
            </SphCallout>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                <div class="flex flex-col gap-4">
                    <SphField
                        :label="i18n.t('Einrichtungs-Administrator')"
                        :errors="errors.admin"
                        required
                    >
                        <!--
                            SphSelect fits its button to the selected name. Every select here should be as wide as the
                            longest label instead, so the wrapper sets the width and stretches the select's w-fit box.
                        -->
                        <div class="min-w-59 max-w-59 [&_.sph-select>div]:w-full">
                            <SphSelect
                                :model-value="selected('admin')"
                                :options="adminOptions"
                                :null-option="false"
                                :disabled="!canChangeAdmin"
                                @update:model-value="select('admin', $event)"
                            />
                        </div>
                    </SphField>

                    <SphField
                        v-for="field in management"
                        :key="field.role"
                        :label="i18n.t(field.label)"
                        :errors="errors[field.role]"
                        :required="field.role === 'executive'"
                    >
                        <div class="min-w-59 max-w-59 [&_.sph-select>div]:w-full">
                            <SphSelect
                                :model-value="selected(field.role)"
                                :options="optionsFor(field.role)"
                                :null-option="field.role === 'executive' ? false : ''"
                                @update:model-value="select(field.role, $event)"
                            />
                        </div>
                    </SphField>
                </div>

                <div class="flex flex-col gap-4">
                    <SphField
                        v-for="field in officers"
                        :key="field.role"
                        :label="i18n.t(field.label)"
                        :errors="errors[field.role]"
                    >
                        <div class="min-w-59 max-w-59 [&_.sph-select>div]:w-full">
                            <SphSelect
                                :model-value="selected(field.role)"
                                :options="employeeOptions"
                                @update:model-value="select(field.role, $event)"
                            />
                        </div>
                    </SphField>
                </div>
            </div>

            <div class="flex justify-end">
                <SphButton
                    :label="i18n.t('save')"
                    variant="primary"
                    :disabled="pending"
                    @click="save()"
                />
            </div>
        </div>
    </div>
</template>
